<template>
  <div style="max-width: 100rem; margin: auto;">
    <b-card>
      <b-card-header>
        <h1 class="info">
          <feather-icon
            icon="BookOpenIcon"
            size="24"
          >
          </feather-icon>
          Terms of Service 
        </h1>
      </b-card-header>
      <b-card-body>
        <h2>TERMS OF USE</h2>
        <p><em>Last Updated: July 23, 2025</em></p>
        
        <h4>1. General Introduction</h4>
        <p>Welcome to ShadowTech. By accessing or using any products, services, websites, or systems provided by us, you confirm that you have read, understood, and agreed to the following Terms of Use. You also confirm that you are of legal age and have the legal capacity to use our services.</p>

        <h4>2. Permitted Legal Use</h4>
        <p>All ShadowTech products are designed and provided solely for lawful purposes, including: advertising campaign testing, SEO performance optimization, offer status analysis, user experience measurement, market research, or software development.</p>
        <p>Users are responsible for ensuring they have lawful rights to any campaigns or platforms where they apply our tools for testing purposes. ShadowTech is not responsible for verifying the legality or authorization of the platforms or environments tested by users. ShadowTech reserves the right to request written proof of ownership or authorization before allowing access to certain advanced testing features.</p>
        <p>You agree not to use the services in any way that may constitute:</p>
        <ul>
          <li>Violation of Vietnamese or international laws</li>
          <li>Unauthorized interference with third-party platforms</li>
          <li>Simulation of real user behavior for fraudulent or manipulative purposes, except for lawful testing by the user on owned or authorized environments</li>
          <li>Harm to the reputation or rights of other individuals or organizations</li>
          <li>Unauthorized access to or exploitation of network or system resources</li>
          <li>Violations of Article 12 of the 2018 Vietnam Cybersecurity Law</li>
          <li>Violations of any third-party platform terms (e.g., Google, Meta, TikTok, Apple)</li>
          <li>Bypassing or manipulating identity verification, moderation, or platform security systems</li>
          <li>Distribution of malware, system disruption, denial-of-service attacks (DDoS), inciting public disorder, or dissemination of hateful, violent, or discriminatory content</li>
        </ul>

        <h4>3. Limitation of Liability</h4>
        <p>Users are solely responsible for any misuse of ShadowTech products. ShadowTech shall not be liable for any consequences resulting from misuse, legal violations, or breach of terms. In the event of an issue arising from a fault in systems directly operated by ShadowTech, and such fault is clearly verified to have caused damage, we will consider reasonable compensation within the scope of the agreement and in accordance with applicable law. In any case, ShadowTech's total liability (if any) shall not exceed the total amount paid by the user within the 12 months prior to the dispute.</p>
        <p>Users agree to indemnify and hold harmless ShadowTech, its personnel, and partners from any claims, losses, or damages arising from legal violations, breach of terms, or harm to third-party interests in connection with their use of the service.</p>
        <p>We reserve the right to refuse service or suspend user accounts if illegal usage is detected or upon request from competent legal authorities.</p>

        <h4>4. Intellectual Property and Ownership</h4>
        <p>All products, content, source code, interfaces, operational processes, and tools developed by ShadowTech are our intellectual property. Users are granted usage rights based on the quantity and duration purchased, and any copying, reuse, or redistribution without prior written consent is a legal violation.</p>
        <p>Some parts of the products may contain open-source components licensed under separate terms, which do not affect ShadowTech's overall intellectual property rights.</p>
        <p>Users are responsible for any content they create or upload during their use of the service.</p>
        <p>ShadowTech does not claim ownership of user-generated content but reserves the right to use such content when necessary to operate or protect the service.</p>

        <h4>5. Right to Amend</h4>
        <p>We may update these Terms when necessary and will notify users at least 7 days prior to the effective date via website or email. Continued use of the services after this period indicates acceptance of the changes.</p>

        <h4>6. User Rights and Responsibilities</h4>
        <p>Users are responsible for:</p>
        <ul>
          <li>Keeping their account credentials secure</li>
          <li>Not sharing, reselling, or granting access to third parties unless explicitly permitted</li>
          <li>Cooperating fully with competent authorities when required</li>
          <li>Promptly reporting any violations or security threats</li>
        </ul>

        <h4>7. Governing Law and Dispute Resolution</h4>
        <p>In the event of a dispute, both parties shall first seek resolution through negotiation within 30 days. If unresolved, the dispute will be referred to the competent People's Court where ShadowTech is headquartered.</p>

        <h4>8. Force Majeure</h4>
        <p>We are not liable for any service disruption resulting from force majeure events such as natural disasters, military conflicts, unforeseen infrastructure failures, large-scale cyberattacks, or legally mandated actions.</p>

        <h4>9. Refund and Service Cancellation Policy</h4>
        <p>Depending on the product and usage level, ShadowTech may offer refunds in cases of severe technical failures or inability to continue providing the service. Refund requests must be submitted within 7 days of the incident. Terminating service use does not affect provisions regarding ownership, disclaimers, and dispute resolution.</p>

        <hr class="my-4">

        <h2>PRIVACY POLICY</h2>
        <p><em>Last Updated: July 23, 2025</em></p>

        <h4>1. Information We Collect</h4>
        <p>We may collect the following types of information:</p>
        <ul>
          <li>User registration details: email, phone number, username</li>
          <li>Access logs and behavioral data within the system</li>
          <li>IP logs for detecting fraud or abuse</li>
          <li>Cookies and browser-related information (subject to user consent)</li>
          <li>Sensitive data (if applicable), such as location or device info, is collected only when essential for functionality and with explicit user consent</li>
        </ul>
        <p>Personal data is stored for as long as necessary for processing purposes or a maximum of 3 years from the user's last interaction with the service, unless otherwise required by law.</p>

        <h4>2. Purpose of Information Use</h4>
        <ul>
          <li>To verify identity and support users</li>
          <li>To improve product quality</li>
          <li>To detect and prevent abuse or legal violations</li>
          <li>To comply with legal obligations and support investigations upon request by competent authorities</li>
        </ul>

        <h4>3. Information Security</h4>
        <p>We implement both technical and administrative measures to safeguard data. However, no system is absolutely secure. Users should take personal precautions to protect their accounts and avoid sharing sensitive information with unverified third parties.</p>
        <p>In the event of a data breach, ShadowTech will notify affected users and competent authorities within 72 hours of discovery and take appropriate action in compliance with legal requirements.</p>

        <h4>4. User Rights</h4>
        <p>Users have the following rights:</p>
        <ul>
          <li>Access, rectify, or delete personal data</li>
          <li>Withdraw consent at any time</li>
          <li>Request restriction or objection to data processing</li>
          <li>Lodge a complaint with the appropriate data protection authority if privacy is believed to be violated</li>
        </ul>
        <p>Users must verify their identity when submitting such requests, and ShadowTech will respond within 15 business days from receipt of a valid request.</p>

        <h4>5. Commitment Not to Sell Information</h4>
        <p>ShadowTech does not share user data with any third party for behavioral analysis, advertising, or commercial reuse. Certain data may be stored or processed via cloud service providers for performance and security purposes. These providers are bound by strict confidentiality agreements.</p>
        <p>If data is stored or processed on servers located outside of Vietnam, ShadowTech will fully comply with legal requirements on cross-border data transfers and ensure equivalent levels of protection.</p>

        <h4>6. Contact</h4>
        <p>For any questions regarding Privacy, please contact: info@shadowtech.vn</p>

        <div class="mt-4">
          <p><strong>© ShadowTech. All rights reserved.</strong></p>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { BCard, BCardText, BTabs, BTab, BFormTextarea, BBadge, BCardHeader, BCardBody } from 'bootstrap-vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'

export default {
  components: {
    BCard,
    BCardText,
    BTabs,
    BTab,
    BFormTextarea,
    VuePerfectScrollbar,
    AppCollapse,
    AppCollapseItem,
    BBadge,
    BCardHeader,
    BCardBody
  },
  data(){
    return{
      userInfo: this.$store.getters['auth/userInfo']
    }
  },
  setup(){
    const perfectScrollbarSettings = {
      maxScrollbarLength: 120,
      wheelPropagation: false,
      suppressScrollY: true,
      useBothWheelAxes: true
    }

    return {
      perfectScrollbarSettings,
    }
  }
}
</script>

<style>
  .dlborder{
    border-bottom: 1px solid #e9eff5;
  }
  .code{
    overflow-x: auto;
    border-radius: .4rem;
    background-color: #f8fbfd;
    border: 1px solid #e9eff5;
  }
  p.small {
    line-height: 0.5;
  }
</style>
